# RxClass Search API

Search drug classification hierarchies from NLM MED-RT.

## Endpoint

```
GET /v1/rxclass/search
```

## Quick Start

<LanguageBlock>
  <Block language="node">
    ```typescript
    import { Fhirfly } from "@fhirfly-io/terminology";

    const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

    // Search for drug classes related to blood pressure
    const results = await client.rxclass.search({
      q: "angiotensin"
    });

    console.log(`Found ${results.total} classes`);
    for (const item of results.items) {
      console.log(`${item.class_id}: ${item.class_name} (${item.class_type})`);
    }
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/rxclass/search?q=angiotensin"
    ```
  </Block>
</LanguageBlock>

## Parameters

### Text Search

| Parameter | Description | Example |
|-----------|-------------|---------|
| `q` | General text search | `q=angiotensin` |

### Filters

| Parameter | Type | Description | Example |
|-----------|------|-------------|---------|
| `class_type` | string | Classification type | `MoA`, `PE`, `EPC`, `TC`, `SC`, `PK` |

### Pagination & Response

| Parameter | Default | Max | Description |
|-----------|---------|-----|-------------|
| `limit` | 20 | 100 | Results per page |
| `page` | 1 | 100 | Page number |
| `shape` | compact | — | `compact`, `standard`, `full` |
| `sort` | relevance | — | `relevance`, `class_name` |

## Example Response

```json
{
  "facets": {
    "class_type": {
      "EPC": 12,
      "MoA": 8,
      "PE": 5,
      "TC": 3
    }
  },
  "has_more": false,
  "items": [
    {
      "class_id": "N0000175355",
      "class_name": "Angiotensin 2 Receptor Blocker",
      "class_type": "EPC"
    },
    {
      "class_id": "N0000000186",
      "class_name": "Angiotensin-converting Enzyme Inhibition",
      "class_type": "MoA"
    }
  ],
  "limit": 20,
  "meta": {
    "legal": {
      "attribution_required": false,
      "citation": "NLM MED-RT via RxClass. Accessed 2026-03-01 via FHIRfly.",
      "license": "UMLS",
      "source_name": "RxClass"
    }
  },
  "page": 1,
  "total": 28,
  "total_capped": false
}
```

## Use Cases

### Find Mechanisms of Action

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.rxclass.search({
      q: "inhibitor",
      class_type: "MoA"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/rxclass/search?q=inhibitor&class_type=MoA"
    ```
  </Block>
</LanguageBlock>

### Find FDA Pharmacologic Classes

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.rxclass.search({
      class_type: "EPC"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/rxclass/search?class_type=EPC"
    ```
  </Block>
</LanguageBlock>

### Search Physiologic Effects

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.rxclass.search({
      q: "platelet",
      class_type: "PE"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/rxclass/search?q=platelet&class_type=PE"
    ```
  </Block>
</LanguageBlock>

### Find Therapeutic Categories

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.rxclass.search({
      q: "antihypertensive",
      class_type: "TC"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/rxclass/search?q=antihypertensive&class_type=TC"
    ```
  </Block>
</LanguageBlock>

## Facets

RxClass search returns these facets:

| Facet | Description |
|-------|-------------|
| `class_type` | Distribution by classification type (MoA, PE, EPC, TC, SC, PK) |

## Valid Values

### Class Type Values

- `MoA` — Mechanism of Action (how the drug works)
- `PE` — Physiologic Effect (the drug's effect on body processes)
- `EPC` — Established Pharmacologic Class (FDA-designated)
- `TC` — Therapeutic Category (VA therapeutic use)
- `SC` — Structural Class (chemical structure)
- `PK` — Pharmacokinetics (absorption, distribution, metabolism)

## Required Scope

`rxclass.search`

## See Also

- [RxClass Lookup API](/docs/value-apis/rxclass) — Look up a specific drug class
- [RxNorm Search API](/docs/search-apis/rxnorm) — Search drug terminology
- [Search APIs Overview](/docs/search-apis/overview) — Common search features
